<template>
  <div class="exam-instructions" :class="{ 'preview-instructions': preview }">
    <div class="container card">
      <h1 v-if="exam.type === 'practice'" class="h4 practice">
        <b>Practice Exam Instructions</b>
      </h1>
      <h1 v-else class="h4">
        <b>{{ examName }}</b> <br>
        {{ preview ? 'Course Exam' : exam.typeDisplay }}  Instructions
      </h1>
      <slot name="info" />
      <h2 v-if="exam.type === 'track' || exam.type === 'course' || preview" class="h5">
        Please read the following instructions carefully before you proceed.
      </h2>
      <div v-if="exam.type === 'track'" class="instructions">
        <p>The {{ examName }} {{ examName.includes('Career Track') ? '' : 'Career Track' }} Exam is a timed assessment created by the {{ $config.platformName }}. It’s accessible to students who have successfully passed the nine required course exams (seven mandatory and two elective) in the career track.</p>
        <p>All questions carry equal marks and need to be completed in one sitting within the below-specified time frame. A timer will start when you begin the exam. Use it to monitor how much time you have left and distribute it accordingly.</p>
        <p>You need a passing score of 60% to earn the career track certificate.</p>
      </div>
      <div v-else-if="exam.type === 'course' || preview" class="instructions">
        <p>The "{{ examName }}" Course Exam is a timed assessment created by the {{ $config.platformName }}. It‘s accessible to all enrolled students, even if they haven’t completed the entire course or watched any of its content.</p>
        <p>All questions carry equal marks and need to be completed in one sitting within the below-specified time frame. A timer will start when you begin the exam. Use it to monitor how much time you have left and distribute it accordingly.</p>
        <p>You need a passing score of 60% to earn the course certificate.</p>
      </div>
      <h6>Please note the following:</h6>
      <ul class="note">
        <li>
          <div class="body-1">
            {{ preview ? questionsCount : exam.questionsCount }} Questions in Total
            <small>The exam comprises a total of  {{ preview ? caseCount : exam.casesCount }} {{ (exam.casesCount || caseCount) === 1 ? 'case' : 'cases' }}  with {{ preview ? questionsCount : exam.questionsCount || 6 }} questions. The case describes a scenario, followed by X relevant questions. Download the provided data files to answer correctly.</small>
          </div>
        </li>
        <li>
          <div class="body-1">
            {{ preview ? duration : exam.duration }} Minutes in Total
            <small>
              You will have  {{ preview ? duration : exam.duration }} minutes to complete the exam and review your answers. You can monitor the time left at the top of your screen. The timer will start flashing red for the last five minutes. When the clock runs out, your exam will be automatically submitted with the progress you've made up to that moment.
            </small>
          </div>
        </li>
        <li>
          <div class="body-1">
            Flag and Review
            <small>You can answer the questions in any order. If you are unsure about an answer, you can flag, skip, and revisit the question later. You can review and correct your answers at any point during the exam via the Review Questions button in the top right corner. After you receive your evaluation, go through your answers to check which ones were correct/incorrect.</small>
          </div>
        </li>
        <li>
          <div class="body-1">
            Exam Requirements and Code of Conduct
            <small>
              You need a desktop computer or a laptop to take the exam. <br><br>
              Ensure your internet connection is stable, and your equipment is in order. {{ $config.platformName }} will not be responsible for power failures or network issues during the exam.<br><br>
              You are not permitted to take screenshots, record the screen, copy and paste, or distribute the exam content outside the platform in any other way.<br><br>
              The work you submit should be the result of your own unaided efforts.
            </small>
          </div>
        </li>
      </ul>

      <h6>Notes from Instructor</h6>
      <div ref="instructor-notes" class="instructor-notes" v-html="examInstructions.instructions" />

      <div v-if="(examInstructions.technologies && examInstructions.technologies.length) || (examInstructions.packages && examInstructions.packages.length)" class="holder-tools" :class="{ 'technologies': examInstructions.packages && examInstructions.packages.length }">
        <div class="tools">
          <b class="warning-msg"><WarningIcon /> This exam features the following tools which you must install in order to complete the exam.</b>
          <ul class="technologies">
            <li v-for="(technology, i) in examInstructions.technologies" :key="i">
              <components :is="require(`../assets/svg/tech-responsive-${technology.replace(' ', '-').toLowerCase()}.svg?vue-component`)" />
              <span>{{ technology }}</span>
            </li>
          </ul>
          <template v-if="examInstructions.packages && examInstructions.packages.length">
            <p class="install">
              Install these packages:
            </p>
            <ul class="packages">
              <li v-for="([p, link]) in (preview ? packages : exam.packages)" :key="p">
                <component
                  :is="link ? 'a' : 'span'"
                  :href="link"
                  :target="link ? 'blank' : ''"
                >
                  {{ p }}
                </component>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <div v-if="exam.type === 'track' || exam.type === 'course' || preview" class="warning-msg bg">
        <WarningIcon />
        <p>
          <b>Warning:</b>
          <template v-if="exam.type === 'track'">
            You must receive an evaluation before you can quit this exam. If you close your browser or lose your internet connection and return to the exam later, the timer will not pause. So, we recommend you take the exam in one sitting. If you fail on your first attempt, you must watch at least 30 minutes of content content from any of the nine required courses before retaking the exam to unlock a second attempt. If you fail again, the exam will remain locked for three months before you can retake it. You’ll get a fourth attempt after another three months if needed. Note that the exam content will differ every time.
          </template>
          <template v-else-if="exam.type === 'course' || preview">
            You must receive an evaluation before you can quit this exam. If you close your browser or lose internet connection and return to the exam later, the timer will not pause. So, we recommend you take the exam in one sitting. You have an unlimited number of attempts. But you must watch 30 minutes of the course content to unlock the next one.
          </template>
          <template v-else-if="exam.type === 'practice'">
            You cannot quit this exam without receiving an evaluation. If you close your browser or lose internet connection and return to the exam later, the timer will not pause. Thus, we recommend that you take the exam in one sitting. You have unlimited number of attempts and there are no requirements as to when you can retake the exam. However, it is in your best interest to pay attention to the answers marked as incorrect and watch the relevant theoretical or practical part of the course.
          </template>
        </p>
      </div>

      <p v-if="exam.type === 'track' || exam.type === 'course' || preview">
        By attempting this exam, you acknowledge that you have read and understand the instructions and respect the code of conduct requirements.
      </p>

      <div v-if="!preview" class="btn-group">
        <button class="exit btn-light-shade" @click="$router.go(-1)">
          Exit
        </button>
        <button class="start btn-primary" :disabled="$store.state.exams.examProcessing === exam.id" @click="$store.dispatch('exams/start')">
          Start Exam
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Prism from 'prismjs';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-cpp';
import global from '../mixins/global';
import QuestionIcon from '../assets/svg/icon-exam-question.svg?vue-component';
import ClockIcon from '../assets/svg/icon-exam-clock.svg?vue-component';
import FlagIcon from '../assets/svg/icon-exam-flag.svg?vue-component';
import WarningIcon from '../assets/svg/icon-exam-warning.svg?vue-component';

export default {
  components: {
    QuestionIcon, ClockIcon, FlagIcon, WarningIcon,
  },
  mixins: [global],
  props: {
    summary: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    preview: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...mapState({ exam: (state) => state.exams.exam }),
    duration() {
      const durationInMinutes = (this.summary.timeout.hours * 60) + this.summary.timeout.minutes;
      return durationInMinutes;
    },
    caseCount() {
      const { examCases } = this.$store.state['course-builder'].active;
      return examCases && examCases.length;
    },
    questionsCount() {
      const { examCases } = this.$store.state['course-builder'].active;
      let totalQuestions = 0;

      examCases.forEach((caseItem) => {
        caseItem.assets.forEach((asset) => {
          if (asset.type === 'exam-question') {
            totalQuestions += 1;
          }
        });
      });
      return totalQuestions;
    },
    packages() {
      const packagesString = this.summary.packages;
      const packagesArray = packagesString.split(',').map((p) => {
        const [name] = p.split('|');
        return [name];
      });

      return packagesArray;
    },
    examInstructions() {
      if (this.preview) {
        return this.summary;
      }
      return this.exam;
    },
    examName() {
      if (this.preview) {
        return this.$store.state['course-builder'].active.name;
      }
      return this.exam.name;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.highlightCode();

      if (this.$refs['instructor-notes']) {
        this.loadGallery([this.$refs['instructor-notes']]);
      }
    });
  },
  methods: {
    highlightCode() {
      const code = this.$el.querySelectorAll('pre code:not([class*="language-"])');

      code.forEach((block) => {
        Prism.highlightElement(block);
      });
    },
  },
};
</script>

<style lang="scss">
.exam-instructions {
  height: 100%;
  padding: var(--spacing-70) 0 var(--spacing-80);
  background: var(--main-dark-shade-1);

  .card {
    padding: var(--spacing-60) 52px var(--spacing-80);
    background: var(--main-light);
    border: 1px solid var(--main-light-shade-2);
    border-radius: 10px;

    .practice {
      b {
        font-size: 28px;
        font-weight: 500;
      }
    }

    h1 {
      font-weight: 700;
      text-align: center;
      color: var(--main-dark-light-3);
      margin-bottom: 64px;

      b {
        color: var(--main-dark-shade-0);
      }
    }

    h5 {
      margin-bottom: var(--spacing-20);
    }

    h6 {
      font-weight: 600;
    }

    .instructions {
      margin-bottom: var(--spacing-32);
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        display: flex;
        margin-bottom: var(--spacing-32);

        .icon {
          margin-right: var(--spacing-16);
          width: 32px;
        }

        .body-1 {
          line-height: 20px;

          small {
            color: var(--main-dark-shade-1);
            display: block;
          }
        }
      }
    }

    ul.note {
      li {
        position: relative;
        padding-left: 56px;

        &:before {
          content: "";
          position: absolute;
          top: 4px;
          left: 32px;
          width: 8px;
          height: 8px;
          background: var(--main-light-shade-4);
          border-radius: 50%;
        }

        .body-1 {
          color: var(--main-dark-shade-0);

          > small {
            color: var(--main-dark-shade-1);
          }
        }
      }
    }

    .warning-msg {
      display: flex;
      align-items: flex-start;

      &.bg {
        background-color: var(--main-light-shade-1);
        border: 1px solid var(--main-light-shade-3);
        padding: var(--spacing-18) var(--spacing-32);
        border-radius: 10px;
        margin-top: 40px;
        margin-bottom: var(--spacing-32);
        align-items: flex-start;
      }

      svg {
        margin-right: var(--spacing-8);
        flex: 0 0 auto;
      }

      p {
        margin: 0;
      }
    }

    .btn-group {
      margin-top: var(--spacing-80);
      display: flex;
      align-items: center;
      justify-content: space-between;

      .start {
        width: 224px;
      }

      .exit {
        width: 112px;
      }
    }

    .instructor-notes {
      code:not([class*="language-"]) {
        background: #F5F2F0;
        border: 1px solid #D0CFCE;
        padding: 1px 3px;
        border-radius: 5px;
        color: var(--main-red);
        display: inline-block;
        min-width: 30px;
        line-height: 20px;
        vertical-align: middle;
      }

      code[class*="language-"] {
        span {
          background: #f5f2f0;
        }
      }

      pre {
        margin: 0;
        border-radius: 10px;
        padding: 12px;
        border: 1px solid #D0CFCE;
      }
    }

    .instructor-notes {
      pre svg {
        display: none;
      }
    }
  }

  .holder-tools {
    background-color: var(--main-light-shade-1);
    border: 1px solid var(--main-light-shade-3);
    border-radius: 10px;
    padding: var(--spacing-20) var(--spacing-6) var(--spacing-16) var(--spacing-32);
    font-size: 18px;
    margin-top: 40px;

    &.technologies {
      padding-bottom: 10px;
    }

    .tools {
      max-height: 267px;
      overflow: auto;
      scrollbar-color: (var(--main-light-shade-4) var(--main-light-shade-1));
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        width: 5px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: var(--main-light-shade-4);
        border-radius: 100px;
      }

      p {
        font-size: 18px;
      }

      .install {
        margin-left: 40px;
      }

      ul.technologies {
        padding: 0;
        margin: 0;
        display: flex;
        margin-left: 40px;
        margin-top: 16px;

        li {
          font-size: 14px;
          color: var(--darker-gray);
          margin-right: var(--spacing-28);
          margin-bottom: 0;

          svg {
            width: 20px;
            margin-right: var(--spacing-8);

            path {
              fill: var(--accent);
            }
          }
        }
      }

      ul.packages {
        font-size: 14px;
        margin-left: 72px;
        padding-left: var(--spacing-32);

        li {
          line-height: 23px;
          margin-bottom: var(--spacing-4);
          position: relative;
          font-weight: 500;

          :before {
            content: "";
            position: absolute;
            left: -18px;
            top: 50%;
            transform: translateY(-50%);
            background: var(--main-light);
            border: 2px solid var(--main-light-shade-4);
            border-radius: 50%;
            width: 6px;
            height: 6px;
          }
        }
      }
    }

    code:not(.hljs) {
      background: #E8E8E8;
      padding: 1px 3px;
      border-radius: 3px;
    }
  }

  &.preview-instructions {
    background: unset;
    padding: 0;

    .card {
      width: unset;
      padding: unset;
      background: unset;
      border: unset;

      h1 {
        margin-bottom: var(--spacing-40);
      }

      h2 {
        margin-bottom: var(--spacing-20);
      }

      .instructions {
        margin-bottom: var(--spacing-40);
      }

      ul.note li:last-of-type {
        margin-bottom: var(--spacing-40);
      }
    }
  }
}

.fa {
  .exam-instructions .card ul.note li {
    &:before {
      background: var(--main-dark-light-4);
    }

    .body-1 > small {
      color: var(--background-700);
    }
  }
}
</style>
